import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquare } from 'lucide-react';

import type { Comment } from '@tasks/contracts';

import { createComment, deleteComment, listComments, updateComment } from '../../api/comments';
import { queryKeys } from '../../api/queryKeys';
import { Avatar } from '../../components/Avatar';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { MarkdownEditor } from '../../components/MarkdownEditor';
import { MarkdownView } from '../../components/MarkdownView';
import { Skeleton } from '../../components/Skeleton';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../auth/AuthProvider';
import { humanizeError } from '../../lib/errors';
import { formatRelative } from '../../lib/format';
import styles from './IssueCommentsTab.module.css';

/** Комментарии: optimistic-отправка (UC-COM-01), markdown, редактирование. */
export function IssueCommentsTab({ issueKey }: { readonly issueKey: string }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  const query = useQuery({
    queryKey: queryKeys.issues.comments(issueKey),
    queryFn: ({ signal }) => listComments(issueKey, { limit: 100, offset: 0 }, signal),
  });

  const createMutation = useMutation({
    mutationFn: (body: string) => createComment(issueKey, body),
    onSuccess: (comment) => {
      setDraft('');
      queryClient.setQueryData<{ items: readonly Comment[]; total: number; limit: number; offset: number }>(
        queryKeys.issues.comments(issueKey),
        (previous) =>
          previous
            ? { ...previous, items: [...previous.items, comment], total: previous.total + 1 }
            : previous,
      );
    },
    onError: (cause: unknown) => {
      const human = humanizeError(cause);
      toast.error(human.message, { code: human.code });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (variables: { readonly id: string; readonly body: string }) =>
      updateComment(variables.id, { body: variables.body }),
    onSuccess: (comment) => {
      setEditingId(null);
      queryClient.setQueryData<{ items: readonly Comment[]; total: number; limit: number; offset: number }>(
        queryKeys.issues.comments(issueKey),
        (previous) =>
          previous
            ? { ...previous, items: previous.items.map((item) => (item.id === comment.id ? comment : item)) }
            : previous,
      );
    },
    onError: (cause: unknown) => {
      toast.error(humanizeError(cause).message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteComment(id),
    onSuccess: (_result, id) => {
      queryClient.setQueryData<{ items: readonly Comment[]; total: number; limit: number; offset: number }>(
        queryKeys.issues.comments(issueKey),
        (previous) =>
          previous
            ? { ...previous, items: previous.items.filter((item) => item.id !== id), total: previous.total - 1 }
            : previous,
      );
    },
    onError: (cause: unknown) => {
      toast.error(humanizeError(cause).message);
    },
  });

  const comments = query.data?.items ?? [];

  return (
    <div className={styles['tab']}>
      <MarkdownEditor
        value={draft}
        placeholder="Написать комментарий… @логин для упоминания"
        ariaLabel="Новый комментарий"
        minRows={3}
        isSubmitting={createMutation.isPending}
        submitLabel="Отправить"
        onChange={setDraft}
        onSubmit={() => {
          if (draft.trim().length === 0) {
            return;
          }
          createMutation.mutate(draft);
        }}
      />

      {query.isPending ? (
        <Skeleton variant="row" count={3} />
      ) : comments.length === 0 ? (
        <EmptyState
          compact
          icon={<MessageSquare size={18} />}
          title="Комментариев пока нет"
          description="Начните обсуждение — автор задачи увидит его сразу."
        />
      ) : (
        <ul className={styles['list']}>
          {comments.map((comment) => {
            const isAuthor = comment.author.id === user?.id;
            return (
              <li key={comment.id} className={styles['comment']}>
                <Avatar user={comment.author} size="sm" />
                <div className={styles['body']}>
                  <div className={styles['meta']}>
                    <span className={styles['author']}>{comment.author.displayName}</span>
                    <span className={styles['time']}>{formatRelative(comment.createdAt)}</span>
                    {comment.editedAt ? <span className={styles['edited']}>изменён</span> : null}
                    <span className={styles['spacer']} />
                    {isAuthor ? (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingId(comment.id);
                            setEditDraft(comment.body);
                          }}
                        >
                          Изменить
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteMutation.mutate(comment.id)}
                          loading={deleteMutation.isPending && deleteMutation.variables === comment.id}
                        >
                          Удалить
                        </Button>
                      </>
                    ) : null}
                  </div>
                  {editingId === comment.id ? (
                    <MarkdownEditor
                      value={editDraft}
                      autoFocus
                      minRows={3}
                      ariaLabel="Редактирование комментария"
                      submitLabel="Сохранить"
                      isSubmitting={updateMutation.isPending}
                      onChange={setEditDraft}
                      onSubmit={() => updateMutation.mutate({ id: comment.id, body: editDraft })}
                      onCancel={() => setEditingId(null)}
                    />
                  ) : (
                    <MarkdownView value={comment.body} />
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
